import React, { useState, useEffect } from "react";
import OutageNoteDialog from "./OutageNoteDialog";

import {
    Grid,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Box,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CommentIcon from "@mui/icons-material/Comment";

import moment from "moment";
import { telemetryService } from "../../../services/TelemetryService";
import { useNavigate } from "react-router-dom";

const classes = {
    container: {
        marginBottom: "1rem",
    },
    offlineTitle: {
        fontWeight: "700",
        fontSize: "1.4rem",
        marginBottom: ".5rem",
        textAlign: "center",
        display: "flex",
    },
    deviceId: {
        fontSize: "1rem",
        fontWeight: "700",
        marginBottom: ".5rem",
        "&:hover": {
            cursor: "pointer",
            textDecoration: "none",
        },
    },
    tableCell: {
        padding: "0px !important",
    },
    viewDetailLink: {
        fontSize: ".8rem",
        position: "absolute",
        right: "1rem",
        "&:hover": {
            cursor: "pointer",
        },
    },
    dateSelectorContainer: {
        margin: "auto",
        width: "80%",
        marginBottom: "2rem",
    },
    offlineChartHeader: {
        width: "33%",
    },
    visibilityIconContainer: {
        display: "flex",
        justifyContent: "flex-end",
        position: "relative",
    },
    noteSection: {
        display: "flex",
        justifyContent: "space-evenly",
        "&:hover": {
            cursor: "pointer",
        },
    },
};

export default function OfflineEdgeDevices({ deviceType, loading, offlineDevices, updateOfflineDevices }) {
    const navigate = useNavigate();
    const [viewDetail, setViewDetail] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);
    const [openDeviceId, setOpenDeviceId] = useState(null);
    const [outageNote, setDialogOutageNote] = useState("");
    const [devices, setDevices] = useState([]);

    useEffect(() => {
        setDevices(offlineDevices);
    }, [deviceType, offlineDevices]);

    const handleLink = (e, deviceId) => {
        e.preventDefault();

        let device = devices.find((dev) => dev.edge_device_id === deviceId);
        let endDateTime = device.dttm_last_report;
        let deviceUrl = deviceId < 500000 ? `v3/${deviceId}?${endDateTime}` : `polaris/${deviceId}`;
        let path = `/telemetry/device-health/${deviceUrl}`;

        navigate(path, { endDateTime });
    };

    const addLineBreak = (value, type) => {
        if (type === "late") {
            if (value.includes("days")) {
                let strArr = value.split("days ");
                return (
                    <div>
                        <p style={{ margin: 0 }}>{strArr[0] + "days"}</p>
                        <p style={{ margin: 0 }}>{strArr[1]}</p>
                    </div>
                );
            } else if (value.includes("day")) {
                let strArr = value.split("day ");
                return (
                    <div>
                        <p style={{ margin: 0 }}>{strArr[0] + "day"}</p>
                        <p style={{ margin: 0 }}>{strArr[1]}</p>
                    </div>
                );
            } else {
                return value;
            }
        } else if (type === "report") {
            let strArr = value.split(" ");
            let dateStr = strArr.slice(0, 3).join(" ");
            let timeStr = strArr[3];

            return (
                <div>
                    <p style={{ margin: 0 }}>{dateStr}</p>
                    <p style={{ margin: 0 }}>{timeStr}</p>
                </div>
            );
        } else if (type === "name") {
            return (
                <div>
                    {value
                        .map((v, idx) => (
                            <p key={v + idx} style={{ margin: 0 }}>
                                {v}
                            </p>
                        ))
                        .reduce((acc, el) => [acc, el])}
                </div>
            );
        }
    };

    const handleViewDetail = () => {
        setViewDetail(!viewDetail);
    };

    const handleDialogOpen = (bool, edge_device_id, outageNote) => {
        setOpenDeviceId(edge_device_id);
        setOpenStatus(bool);
        setDialogOutageNote(outageNote);
    };

    const handleCreateNote = (note) => {
        if ((note || note === "") && openDeviceId) {
            telemetryService.upsertOutageNote(openDeviceId, note);
            updateOfflineDevices(openDeviceId, note);
        }
    };

    return (
        <Box component="div" sx={classes.container}>
            {!loading && devices?.length ? (
                <>
                    <Box component="div" sx={classes.offlineTitle}>
                        <Box component="div" sx={classes.offlineChartHeader}></Box>
                        <Box component="div" sx={classes.offlineChartHeader}>
                            Offline Devices
                        </Box>
                        <Box component="div" sx={{ ...classes.offlineChartHeader, ...classes.visibilityIconContainer }}>
                            <Tooltip title={viewDetail ? "Hide Last Report Details" : "View Last Report Details"}>
                                <Link sx={classes.viewDetailLink} onClick={handleViewDetail}>
                                    {viewDetail ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </Link>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Grid item xs={12} md={12} lg={12}>
                        <TableContainer component={Paper}>
                            <Table sx={classes.table} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">
                                            Device ID /<br />
                                            Serial No.
                                        </TableCell>
                                        <TableCell align="center">
                                            Site
                                            <br />
                                            Name
                                        </TableCell>
                                        <TableCell align="center">
                                            Last Report
                                            <br />
                                            (GMT+0)
                                        </TableCell>
                                        <TableCell align="center">
                                            Outage
                                            <br />
                                            Duration
                                        </TableCell>
                                        <TableCell align="center">
                                            Outage
                                            <br />
                                            Note
                                        </TableCell>
                                        {viewDetail && (
                                            <>
                                                <TableCell align="center">
                                                    Voltage
                                                    <br />
                                                    (V)
                                                </TableCell>
                                                <TableCell align="center">
                                                    Point
                                                    <br />
                                                    Count
                                                </TableCell>
                                                <TableCell align="center">
                                                    Ambient
                                                    <br />
                                                    Temp (C)
                                                </TableCell>
                                                <TableCell align="center">
                                                    Lidar
                                                    <br />
                                                    Temp (C)
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {devices?.map((row) => {
                                        let {
                                            dttm_last_report,
                                            late_duration,
                                            edge_device_id,
                                            short_name,
                                            cloudgate_serial,
                                            ambientTempC,
                                            cg_supply_voltage,
                                            compressed_count,
                                            lidarTempC,
                                            note,
                                        } = row;

                                        let lastDttm = moment(dttm_last_report).utc().format("MMM DD, YYYY HH:mm:ss");
                                        let lateReportDttm = late_duration.split(".")[0];
                                        let voltage =
                                            cg_supply_voltage && cg_supply_voltage >= 0
                                                ? (cg_supply_voltage / 1000).toFixed(2)
                                                : "";
                                        let outageNote = note;

                                        return (
                                            <TableRow key={cloudgate_serial + edge_device_id}>
                                                <TableCell id="tableCell" component="th" scope="row" align="center">
                                                    <Link
                                                        onClick={(e) => handleLink(e, edge_device_id)}
                                                        sx={classes.deviceId}
                                                    >
                                                        {edge_device_id} /
                                                        <br />
                                                        {cloudgate_serial}
                                                    </Link>
                                                </TableCell>
                                                <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                    {addLineBreak(short_name, "name")}
                                                </TableCell>
                                                <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                    {addLineBreak(lastDttm, "report")}
                                                </TableCell>
                                                <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                    {addLineBreak(lateReportDttm, "late")}
                                                </TableCell>
                                                <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                    <Box component="div" sx={classes.noteSection}>
                                                        <Tooltip title={outageNote || "Create Outage Note"}>
                                                            <CommentIcon
                                                                onClick={() =>
                                                                    handleDialogOpen(true, edge_device_id, outageNote)
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                {viewDetail && (
                                                    <>
                                                        <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                            {voltage}
                                                        </TableCell>
                                                        <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                            {compressed_count}
                                                        </TableCell>
                                                        <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                            {ambientTempC}
                                                        </TableCell>
                                                        <TableCell sx={classes.tableCell} id="tableCell" align="center">
                                                            {lidarTempC}
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </>
            ) : null}

            <OutageNoteDialog
                type={"status"}
                openStatus={openStatus}
                handleDialogOpen={handleDialogOpen}
                handleCreateNote={handleCreateNote}
                deviceId={openDeviceId}
                outageNote={outageNote}
            />
        </Box>
    );
}
